import React, { useState, useEffect } from 'react';
import { Activity, Clock, CheckCircle2, AlertTriangle, RefreshCw, Database, ChevronRight } from 'lucide-react';
import { useChat } from '../context/ChatContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtRelative = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec/60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec/3600)}h ${Math.floor((sec%3600)/60)}m ago`;
    return `${Math.floor(sec/86400)}d ago`;
  } catch { return '—'; }
};

const fmtCountdown = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const sec = Math.floor((d.getTime() - Date.now()) / 1000);
    if (sec <= 0) return 'imminent';
    if (sec < 60) return `in ${sec}s`;
    if (sec < 3600) return `in ${Math.floor(sec/60)}m`;
    return `in ${Math.floor(sec/3600)}h ${Math.floor((sec%3600)/60)}m`;
  } catch { return '—'; }
};

const fmtAbs = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
      timeZone: 'Asia/Kolkata',
    }) + ' IST';
  } catch { return '—'; }
};

export default function SyncStatusBanner() {
  const { selectedBrand } = useChat();
  const [data, setData] = useState(null);
  const [, tick] = useState(0);

  // Determine user role from /api/auth/me
  const [userRole, setUserRole] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
        const j = await r.json();
        setUserRole(j?.user?.role || null);
      } catch { /* swallow */ }
    })();
  }, []);

  const isSuperAdmin = userRole === 'super_admin' || userRole === 'admin';

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Per-brand mode: a specific brand is selected
        // Global mode: ONLY for super admin AND no specific brand selected
        const useGlobal = isSuperAdmin && !selectedBrand?.brand_id;
        const url = useGlobal
          ? `${API_URL}/api/sync-status/`
          : (selectedBrand?.brand_id ? `${API_URL}/api/sync-status/${selectedBrand.brand_id}` : null);
        if (!url) { setData(null); return; }
        const res = await fetch(url, { credentials: 'include' });
        const j = await res.json();
        if (j.success) setData(j);
      } catch (e) { /* swallow */ }
    };
    fetchData();
    const interval = setInterval(fetchData, 30000); // 30s polling
    return () => clearInterval(interval);
  }, [selectedBrand?.brand_id, isSuperAdmin]);

  // Tick every 1s for live "ago" / countdown updates
  useEffect(() => {
    const t = setInterval(() => tick(n => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  if (!data) return null;

  // Per-brand mode
  if (selectedBrand?.brand_id && data.brand_id) {
    const fresh = data.data_freshness || {};
    const sync = data.sync_history || {};
    const sched = data.scheduler || {};
    const status = sync.status || 'unknown';
    const ok = status === 'success' || status === 'completed';
    const inProgress = status === 'in_progress' || status === 'running';

    return (
      <div className="rounded-lg border border-border bg-card/50 backdrop-blur px-4 py-2.5 mb-4 flex items-center gap-4 flex-wrap" data-testid="sync-status-banner">
        <Item icon={ok ? CheckCircle2 : inProgress ? RefreshCw : AlertTriangle}
              iconClass={ok ? 'text-emerald-400' : inProgress ? 'text-blue-400 animate-spin' : 'text-amber-400'}
              label="Last sync"
              value={fmtRelative(sync.last_sync_at)}
              tooltip={fmtAbs(sync.last_sync_at)} />
        <Divider />
        <Item icon={Database} iconClass="text-blue-400"
              label="Latest data"
              value={fresh.latest_transaction_date ? fmtAbs(fresh.latest_transaction_date) : 'N/A'}
              tooltip="Most recent transaction datetime in local DB" />
        <Divider />
        <Item icon={Clock} iconClass="text-purple-400"
              label="Next sync"
              value={fmtCountdown(sched.next_run_at)}
              tooltip={fmtAbs(sched.next_run_at)} />
        <Divider />
        <Item icon={Activity} iconClass="text-pink-400"
              label="Counts"
              value={`${(data.counts?.transactions || 0).toLocaleString('en-IN')} txns · ${(data.counts?.customers || 0).toLocaleString('en-IN')} cust`} />
        {sync.error && (
          <span className="text-xs text-rose-400 truncate max-w-md">⚠ {sync.error}</span>
        )}
      </div>
    );
  }

  // Global mode (super admin / all-brands)
  const sched = data.scheduler || {};
  const totalBrands = (data.brands || []).length;
  const stale = (data.brands || []).filter(b => {
    if (!b.last_sync_at) return true;
    try { return (Date.now() - new Date(b.last_sync_at).getTime()) > 24*3600*1000; }
    catch { return true; }
  }).length;

  return (
    <div className="rounded-lg border border-border bg-card/50 backdrop-blur px-4 py-2.5 mb-4 flex items-center gap-4 flex-wrap" data-testid="sync-status-banner-global">
      <Item icon={CheckCircle2} iconClass="text-emerald-400"
            label="Global last sync"
            value={fmtRelative(sched.last_run_at)}
            tooltip={fmtAbs(sched.last_run_at)} />
      <Divider />
      <Item icon={Clock} iconClass="text-purple-400"
            label="Next sync"
            value={fmtCountdown(sched.next_run_at)}
            tooltip={fmtAbs(sched.next_run_at)} />
      <Divider />
      <Item icon={Activity} iconClass="text-pink-400"
            label="Brands"
            value={`${totalBrands} total · ${stale} stale (>24h)`} />
      <Divider />
      <Item icon={Database} iconClass="text-blue-400"
            label="Cadence"
            value={`Every ${sched.interval_hours || 4}h`} />
    </div>
  );
}

const Item = ({ icon: Icon, iconClass = '', label, value, tooltip }) => (
  <div className="flex items-center gap-2" title={tooltip || ''}>
    <Icon className={`w-4 h-4 flex-shrink-0 ${iconClass}`} />
    <div className="text-xs leading-tight">
      <div className="text-muted-foreground uppercase tracking-wider text-[10px]">{label}</div>
      <div className="font-mono font-semibold text-foreground">{value}</div>
    </div>
  </div>
);

const Divider = () => <ChevronRight className="w-3 h-3 text-muted-foreground/40 hidden md:block" />;
